import { useEffect } from "react";
import { useState, useCallback, useMemo } from "react";
import { t } from "i18next";
import { Pagination } from "../../components/pagination/Pagination";
import PartsTable from "./sections/PartsTable";

import "./Parts.scss";
import PartsFilterForm, { getQueriesFromFilter } from "./filter/PartsFilterForm";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import {
  SearchOptions,
  toSearchOrder,
  searchWithFilters,
} from "../../services/api/search";
import { getPartExportDescriptor } from "../../services/export/data/partExport";
import ExportButton from "../../components/export-button/ExportButton";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import PartForm, { PartFormValidator } from "../../components/data/part/PartForm";
import { validator } from "../../services/form-validator/form-validator";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
import { api } from "../../services/api/api";
import { isOdooConnected } from "../../services/odoo/odoo";
import { useTablePageSize } from "../../hooks/useTablePageSize";

export function Parts() {
  const isNotOdooConnected = !isOdooConnected();

  const [partsPageState, setPartsPageState] = useState<EasyTech.Part[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{head: string, orderDesc: boolean}>({head: "label", orderDesc: false});
  const [filters, setFilters] = useState<Partial<EasyTech.PartsFilterType>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({head, orderDesc});
  };
  
  /** Le tri affiché. Une seule définition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  /**
   * Le descripteur dépend du realm : la colonne Odoo n'apparaît que là où Odoo est branché.
   * Mémorisé pour garder une identité stable, dont la modale se sert pour initialiser la sélection
   * de colonnes.
   */
  const exportDescriptor = useMemo(() => getPartExportDescriptor(), []);

  /**
   * Charge les pièces avec les filtres
   */
  const loadPartsWithFilters = useCallback(async () => {
    setIsLoading(true);
    try {
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries: getQueriesFromFilter(filters),
      };

      const response = await searchWithFilters<EasyTech.Part>(
        "parts",
        searchOptions
      );

      if (response && response.data) {
        setPartsPageState(response.data);
        setTotalItems(response.data.length);
        setPageNumberState(response.totalPages);
      } else {
        setPartsPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des pièces avec filtres:", error);
      setPartsPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize]);


  useEffect(() => {
    loadPartsWithFilters();
  }, [loadPartsWithFilters]);

  /**
  * createVisit: Create new visit event
  * @param data - visit data 
  */
  const createPart = (data: EasyTech.Part): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, PartFormValidator)) {
        api.post("users/parts", data).then((ret) => {
          toast.success("Part created !");
          return resolve();
        }).catch((err) => {
          showApiErrorToast(err);
          return reject(err);
        });
      } else {
        const fieldNames = {
          category: t("common:category"),
          label: t("common:label"),
          price: t("common:sellingPrice"),
          ref: t("common:ref:label"),
          unity: t("common:unity")
        };
        
        const errorMessage = validator.getFormErrorMessage(data, PartFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  /***/

  /**
   * Gère la soumission du filtre
   * @param filterData - Données du filtre
   * @returns Promise<void>
   */
  const handleFilterSubmit = (
    filterData: Partial<EasyTech.PartsFilterType>
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters(filterData || {});
      setPageState(0);
      resolve();
    });
  };

  /**
   * Supprime un filtre spécifique
   * @param field - Champ à supprimer
   * @returns Promise<void>
   */
  const clearFilter = (field: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const newFilters = { ...filters };
      delete newFilters[field as keyof Partial<EasyTech.PartsFilterType>];
      setFilters(newFilters);
      setPageState(0);
      resolve();
    });
  };

  /**
   * Supprime tous les filtres
   * @returns Promise<void>
   */
  const clearAllFilters = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFilters({});
      setPageState(0);
      resolve();
    });
  };

  /**
   * L'export n'est pas conditionné par Odoo : seule la création de pièce l'est, puisque le
   * catalogue est alors maître dans Odoo. Télécharger ce que l'écran affiche reste légitime dans
   * les deux cas — d'où deux conditions distinctes plutôt qu'un seul bloc masqué.
   */
  const headerAction = (
    <>
      <ExportButton<EasyTech.Part> filters={filters} entityName="parts"
        fileName="parts.xlsx"
        descriptor={exportDescriptor} getQueryFromFilter={getQueriesFromFilter}
        order={searchOrder}
      />

      {isNotOdooConnected &&
        <EditCreateDrawer<EasyTech.Part>
          entityName="Part"
          FormComponent={PartForm}
          onSubmit={createPart}
          isNotEditable
        />
      }
    </>
  );

  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.PartsFilterType>
      FormComponent={PartsFilterForm}
      onSubmit={handleFilterSubmit}
      entityName="Parts"
      // Le drawer s'ouvrait vide alors que des filtres étaient actifs. Invisible tant que les
      // champs étaient des zones de texte — on les retapait —, trompeur avec un interrupteur, qui
      // se serait montré éteint sur une liste filtrée.
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={filters}
      onClearFilter={clearFilter}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
      entity="part"
    />
  );

  const mainContent = partsPageState && partsPageState.length > 0 ? (
    <PartsTable parts={partsPageState} onHeadClick={onOrderChange}
    selectedHead={orderState.head} orderDesc={orderState.orderDesc} 
    onPartUpdated={loadPartsWithFilters} />
  ) : (
    <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
      <p className="text-lg text-blue-gray-700">
        {t("piece:noData")}
      </p>
      <p className="text-sm text-blue-gray-500 mt-2">
        {Object.keys(filters).length > 0
          ? t("piece:noData:filters")
          : t("piece:noData:add")}
      </p>
    </div>
  );

  const paginationComponent = totalItems > 0 ? (
    <Pagination
      actualPage={pageState}
      pageNumber={pageNumberState}
      changePage={setPageState}
    />
  ) : null;

  return (
    <GenericListLayout
      entityName={t("common:parts")}
      path="/parts"
      isLoading={isLoading}
      headerActions={headerAction}
      filterDrawer={filterDrawerComponent}
      filterBadges={filterBadgesComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="parts"
    />
  );
}
